import axiosInstance from './AxiosInstance';

const DepositsService = {
  // 1. Create Deposit
  createDeposit: async (depositData) => {
    try {
      const response = await axiosInstance.post('/deposits', depositData);
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create deposit',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 2. Get All Deposits
  getAllDeposits: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);
      if (params.depositorId) queryParams.append('depositorId', params.depositorId);
      if (params.status) queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);
      const response = await axiosInstance.get(`/deposits?${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: response.data.data.data,
        pagination: response.data.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch deposits',
        data: [],
        pagination: {}
      };
    }
  },

  // 3. Get Deposit by ID
  getDepositById: async (id) => {
    try {
      const response = await axiosInstance.get(`/deposits/${id}`);
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch deposit',
      };
    }
  },

  // 4. Update Deposit
  updateDeposit: async (id, updateData) => {
    try {
      const response = await axiosInstance.put(`/deposits/${id}`, updateData);
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update deposit',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 5. Close Deposit
  closeDeposit: async (id) => {
    try {
      const response = await axiosInstance.put(`/deposits/${id}/close`);
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to close deposit',
      };
    }
  },

  // 6. Get Deposit Transaction History
  getDepositHistory: async (id, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.transactionType) queryParams.append('transactionType', params.transactionType);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      const response = await axiosInstance.get(`/deposits/${id}/history?${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: response.data.data.data,
        pagination: response.data.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch deposit history',
        data: [],
        pagination: {}
      };
    }
  },

  // 7. Get Deposits Dropdown
  getDepositsDropdown: async (branchId) => {
    try {
      const response = await axiosInstance.get(`/deposits/dropdown?branchId=${branchId}`);
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch deposits dropdown',
        data: []
      };
    }
  },

  // 8. Get Depositor Deposit Summary
  getDepositorDepositSummary: async (depositorId) => {
    try {
      const response = await axiosInstance.get(`/deposits/depositor/${depositorId}/summary`);
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch deposit summary',
      };
    }
  },

  // 9. Create Deposit Transaction (CREDIT/DEBIT)
  createDepositTransaction: async (depositId, transactionData) => {
    try {
      const response = await axiosInstance.post(`/deposits/${depositId}/history`, transactionData);
      return { 
        success: response.data.success, 
        data: response.data.data, 
        message: response.data.message 
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create transaction',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 10. Get Deposit Interest History
  getDepositInterestHistory: async (id, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      const response = await axiosInstance.get(`/deposits/${id}/interest-history?${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: response.data.data.data,
        pagination: response.data.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch interest history',
        data: [],
        pagination: {}
      };
    }
  },

  // 11. Update Interest Rate
  updateInterestRate: async (depositId, interestData) => {
    try {
      const response = await axiosInstance.post(`/deposits/${depositId}/interest-history`, interestData);
      return { 
        success: response.data.success, 
        data: response.data.data, 
        message: response.data.message 
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update interest rate',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // Get deposits statistics
  getDepositsStatistics: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);

      const url = `/statistics/deposits${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch deposits statistics',
        data: {
          activeDeposits: 0,
          maturedDeposits: 0,
          totalDeposits: 0,
          totalAmount: 0
        }
      };
    }
  }
};

export default DepositsService;
